import { CloseOutlined, UploadOutlined, UserOutlined } from "@ant-design/icons"
import { Avatar, Button, Flex, Form, Typography, Upload } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import { type RcFile, type UploadChangeParam } from "antd/es/upload/interface"
import React, { type ComponentProps, useState } from "react"

import { PhoneInput } from "../../../../../components/PhoneInputs/PhoneInput.tsx"
import { placeholders } from "../../../../../constants/placeholders"
import { validateFile } from "../../../../../utils/file-validation.ts"
import { getBase64 } from "../../../../../utils/utils"
import { validatePhone } from "../../../../../utils/validation/validate-phone.ts"
import { type TeamMembersFilterOptions } from "../../../../TeamManagementPage/redux/team-members.types.ts"
import { type SettingsCompanyFormData } from "../SettingsCompanyForm.tsx"
import { CompanyInformationFormAddress } from "./CompanyInformationFormAddress.tsx"
import { TextArea } from "../../../../../components/Input/TextArea.tsx"
import { TextInput } from "../../../../../components/Input/TextInput.tsx"

const { Text } = Typography

const MAX_SIZE_MB = 10
const ALLOWED_FILE_EXTENSIONS = ["jpeg", "jpg", "png"]

interface CompanyInformationFormSectionProps {
  form: FormInstance<SettingsCompanyFormData>
  initialValues?: SettingsCompanyFormData
  options: TeamMembersFilterOptions | null
  isBranchScope?: boolean
}

// Identity, contact, address, logo and bio all live in one card — the
// original, pre-redesign grouping restored per the client's mockup. Logo
// upload and Bio were briefly split out into a separate "Branding" card;
// that card is gone, its fields folded back in here, not deleted.
export function CompanyInformationFormSection({
  form,
  initialValues,
  options,
  isBranchScope
}: CompanyInformationFormSectionProps) {
  const key: keyof SettingsCompanyFormData = "logo"

  const [logoUrl, setLogoUrl] = useState<string | null>(
    initialValues?.logo && typeof initialValues?.logo === "string"
      ? initialValues?.logo
      : null
  )

  const watchLogo = Form.useWatch(key, form)
  const logoSrc =
    typeof watchLogo === "string" || !watchLogo ? watchLogo : logoUrl

  const onChangeLogo = (info: UploadChangeParam) => {
    const { file } = info

    if (file.status === "done") {
      getBase64(file.originFileObj, (imageUrl: string) => {
        setLogoUrl(imageUrl)
        form.setFieldsValue({ [key]: file.originFileObj })
      })
    }
  }

  const onDeleteLogo = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setLogoUrl(null)
    form.setFieldsValue({ [key]: null })
  }

  const beforeUpload = (file: RcFile) => {
    return validateFile(file, {
      maxSize: MAX_SIZE_MB,
      allowedExtensions: ALLOWED_FILE_EXTENSIONS,
      defaultResult: true,
      sizeMessage: `This logo is larger than ${MAX_SIZE_MB}MB. Please choose a smaller file.`
    })
  }

  const uploadProps: ComponentProps<typeof Upload> = {
    name: key,
    showUploadList: false,
    multiple: false,
    accept: ALLOWED_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(","),
    maxCount: 1,
    beforeUpload: beforeUpload,
    onChange: onChangeLogo,
    customRequest: ({ onSuccess }) => onSuccess && setTimeout(onSuccess, 300)
  }

  return (
    <div className="flex flex-col gap-3">
      <div data-guide-target="company-logo-section">
        <Form.Item<SettingsCompanyFormData>
          label={isBranchScope ? "Branch Logo" : "Company Logo"}
          name={key}
        >
          <Upload {...uploadProps} className={"cursor-pointer"}>
            <Flex gap={"middle"} wrap>
              <div className={"relative"}>
                <Avatar
                  shape="square"
                  size={64}
                  icon={<UserOutlined />}
                  src={logoSrc}
                />
                {logoSrc && (
                  <Button
                    onClick={onDeleteLogo}
                    size={"small"}
                    className={"absolute -bottom-1 -right-1 rounded-md"}
                    icon={<CloseOutlined />}
                  />
                )}
              </div>

              <Flex vertical gap={"small"}>
                <Button icon={<UploadOutlined />}>Upload Logo</Button>
                <Text type={"secondary"} className={"text-xs"}>
                  {ALLOWED_FILE_EXTENSIONS?.join(", ")?.toUpperCase()} • Up to{" "}
                  {MAX_SIZE_MB}MB • Recommended: 512×512 (square)
                </Text>
              </Flex>
            </Flex>
          </Upload>
        </Form.Item>
      </div>

      <div
        data-guide-target="company-details-section"
        className="grid grid-cols-1 gap-x-3 sm:grid-cols-3"
      >
        <Form.Item<SettingsCompanyFormData>
          label={isBranchScope ? "Branch Name" : "Company Name"}
          name="business_name"
          rules={[
            {
              required: true,
              message: isBranchScope
                ? "Please enter the branch name"
                : "Please enter your company name"
            }
          ]}
        >
          <TextInput
            placeholder={
              isBranchScope ? "Enter branch name" : "Enter company name"
            }
          />
        </Form.Item>

        <Form.Item<SettingsCompanyFormData>
          label={"Company Email"}
          name="company_email"
          extra={
            "Where customers write about a signature request or an invoice. Use a shared address, not a person's."
          }
          rules={[{ type: "email", message: "Please enter a valid email" }]}
        >
          <TextInput
            disableAutoCapitalize
            placeholder={"Enter company email"}
          />
        </Form.Item>

        <Form.Item<SettingsCompanyFormData>
          label={"Phone"}
          name="company_phone"
          rules={[{ required: true, validator: validatePhone }]}
        >
          <PhoneInput placeholder={placeholders.phone} />
        </Form.Item>
      </div>

      <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <Form.Item<SettingsCompanyFormData>
            label={"Street Address"}
            name="street_address"
          >
            <TextInput placeholder="Enter address" />
          </Form.Item>
        </div>
        <div className="sm:col-span-1">
          <Form.Item<SettingsCompanyFormData>
            label={"Website"}
            name="company_website"
            rules={[
              {
                type: "url",
                message: "Please enter a valid URL"
              }
            ]}
          >
            <TextInput disableAutoCapitalize placeholder={"Enter website"} />
          </Form.Item>
        </div>
      </div>

      <CompanyInformationFormAddress options={options} />

      <Form.Item<SettingsCompanyFormData>
        label={isBranchScope ? "Branch Bio" : "Company Bio"}
        name="company_description"
      >
        <TextArea
          rows={4}
          placeholder={
            isBranchScope
              ? "Enter a description of this branch's services and expertise..."
              : "Enter a description of your company, services, and expertise..."
          }
        />
      </Form.Item>
    </div>
  )
}
